import { Language } from '~common/i18n';
import {
  arrayTranslations,
  isNilOrEmptyTranslationString,
  mergeTranslations,
} from '~admin/view/components/create-event/helper/merge-translations.helper';
import { EventRestrictionItemFormData } from '~admin/view/components/details/event-details/event-restrictions/event-restriction-item-fields.component';
import { EventProfileCreateRequest } from '~client/model/admin/event-profile-create-request';
import { EventRestrictionTranslationRequest } from '~client/model/admin/event-restriction-translation-request';
import { EventRestrictionTranslationRequestItem } from '~client/model/admin/event-restriction-translation-request-item';
import { EventTranslationRequest } from '~client/model/admin/event-translation-request';
import { EventRestrictionResponse } from '~client/model/event-restriction-response';
import { EventRestrictionType } from '~client/model/event-restriction-type';
import { isNilOrEmpty } from '~common/helper/global';
import { EventFormData } from '~admin/view/components/details/event-form-data';

/**
 * Transforms EventRestrictionsFormData to Partial<EventProfileCreateRequest>
 * @param values
 */
export const getEventRestrictionsRequest = (
  values: EventFormData,
): Pick<EventProfileCreateRequest, 'eventRestrictions'> | null => {
  const { eventRestrictions, merchButtonUrl, merchButtonText, merchText } = values;
  const hasMerchRestriction =
    !isNilOrEmptyTranslationString(merchButtonUrl) || !isNilOrEmptyTranslationString(merchText);
  if (!hasMerchRestriction && isNilOrEmpty(eventRestrictions)) {
    return { eventRestrictions: null };
  }

  const eventRestrictionsRequest = eventRestrictions.map((restriction) => ({
    description: restriction.description[0].value,
    type: EventRestrictionType.DEFAULT,
    url: restriction.url[0].value,
    secondaryDescription: null,
  }));

  if (hasMerchRestriction) {
    eventRestrictionsRequest.push({
      description: merchButtonText[0].value,
      secondaryDescription: merchText[0].value,
      type: EventRestrictionType.MERCHANDISE,
      url: merchButtonUrl[0].value,
    });
  }
  return { eventRestrictions: eventRestrictionsRequest };
};

/**
 * Transforms EventRestrictionItemFormData to EventRestrictionTranslationRequestItem[]
 * @param eventRestriction
 */
const getEventRestrictionItemTranslationsRequest = (
  eventRestriction: EventRestrictionItemFormData,
): EventRestrictionTranslationRequestItem[] => {
  const { description, url } = eventRestriction;

  const eventRestrictionTranslations = new Map<Language, EventRestrictionTranslationRequestItem>();
  mergeTranslations(eventRestrictionTranslations, 'description', description);
  mergeTranslations(eventRestrictionTranslations, 'url', url);

  return arrayTranslations(eventRestrictionTranslations);
};

/**
 * Extracts translation request for merch restrictions.
 * @param values
 */
const getEventMerchandiseRestrictionItemTranslationsRequest = (
  values: EventFormData,
): EventRestrictionTranslationRequestItem[] => {
  const { merchText, merchButtonText, merchButtonUrl } = values;

  const eventRestrictionTranslations = new Map<Language, EventRestrictionTranslationRequestItem>();
  mergeTranslations(eventRestrictionTranslations, 'description', merchButtonText);
  mergeTranslations(eventRestrictionTranslations, 'secondaryDescription', merchText);
  mergeTranslations(eventRestrictionTranslations, 'url', merchButtonUrl);
  return arrayTranslations(eventRestrictionTranslations);
};

/**
 * Transforms EventRestrictionsFormData to Partial<EventTranslationRequest>
 * @param eventRestrictionsResponse
 * @param values
 */
export const getEventRestrictionsTranslationsRequest = (
  eventRestrictionsResponse: EventRestrictionResponse[],
  values: EventFormData,
): Pick<EventTranslationRequest, 'eventRestrictionTranslationRequests'> => {
  if (isNilOrEmpty(eventRestrictionsResponse)) {
    return { eventRestrictionTranslationRequests: null };
  }

  const { eventRestrictions } = values;
  const inAppRestrictions = eventRestrictionsResponse.filter(
    (restriction) => restriction.eventRestrictionType !== EventRestrictionType.MERCHANDISE,
  );
  const restrictionTranslations: EventRestrictionTranslationRequest[] = inAppRestrictions.map(
    (restriction: EventRestrictionResponse, index: number) => ({
      restrictionId: restriction.id,
      eventRestrictionTranslationRequestItems: getEventRestrictionItemTranslationsRequest(
        eventRestrictions[index],
      ),
    }),
  );

  const merchRestriction = eventRestrictionsResponse.find(
    (restriction) => restriction.eventRestrictionType === EventRestrictionType.MERCHANDISE,
  );
  if (!isNilOrEmpty(merchRestriction)) {
    restrictionTranslations.push({
      restrictionId: merchRestriction.id,
      eventRestrictionTranslationRequestItems: getEventMerchandiseRestrictionItemTranslationsRequest(
        values,
      ),
    });
  }

  return { eventRestrictionTranslationRequests: restrictionTranslations };
};



// WEBPACK FOOTER //
// ./view/components/create-event/helper/event-profile-request/event-restrictions-request.helper.ts